<template>
  <component
    :is="component"
    v-model="selectedValues"
    v-bind="$attrs"
    :items="items"
    :label="label"
    :clearable="clearable"
    :placeholder="selectionLabel"
    multiple
    v-on="$listeners"
    @change="toggleItem"
    @considerItem="considerItem"
    @ignoreItem="ignoreItem"
    @click:clear="$emit('clearInfo', label)"
  >
    <template
      v-if="items.length"
      v-slot:prepend-item
    >
      <v-list-item
        class="select-prepend-item"
        ripple
        :class="selectAll.appearance"
        role="option"
        @click="toggleAll"
      >
        <v-list-item-action>
          <v-icon :color="color">
            {{ icon }}
          </v-icon>
        </v-list-item-action>
        <v-list-item-content>
          <v-list-item-title>{{ selectAllLabel }}</v-list-item-title>
        </v-list-item-content>
        <v-list-item-action
          @click.stop
        >
          <v-btn
            icon
            :color="ignoreButtonColor"
            @click.stop="toggleAllToNegativation"
          >
            <StIcon icon-name="cancel" />
          </v-btn>
        </v-list-item-action>
      </v-list-item>
      <v-divider class="mt-2" />
    </template>

    <template v-slot:selection="{index}">
      <span v-if="index === 0">
        {{ selectionLabel }}
      </span>
    </template>

    <template v-slot:item="{item, attrs}">
      <SelectItem
        :item="item"
        :item-attributes="attrs"
        @consider-item="considerItem"
        @ignore-item="ignoreItem"
      />
    </template>
  </component>
</template>

<script>
import { VSelect, VAutocomplete } from 'vuetify/lib';
import StIcon from '@stilingue/st-icon';
import SelectItem from '@/components/filter/SelectItem';
import filtersModel from '@/mixins/filters-model';
import colors from '@/utils/colors';

export default {
  name: 'NegativationFilter',
  components: {
    SelectItem,
    StIcon,
  },
  mixins: [
    filtersModel,
  ],
  props: {
    label: {
      type: String,
      required: true,
    },
    selectAllLabel: {
      type: String,
      default: 'Todos',
    },
    value: {
      type: Object,
      default: () => ({}),
    },
    selectItems: {
      type: Array,
      default: () => [],
    },
    validate: {
      type: Function,
      default: () => ({}),
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    searchable: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      placeholder: 'Todos',
      items: this.getItems(),
      selectedValues: this.getSelectedValues(),
      selectAll: {
        shouldIgnore: false,
        appearance: '',
      },
    };
  },
  computed: {
    component() {
      return this.searchable ? VAutocomplete : VSelect;
    },
    color() {
      const color = 'primary darken-2';

      if (this.allNegativationSelected) return '';
      if (this.allSelected) return color;
      if (this.someSelected) return color;
      return '';
    },
    icon() {
      if (this.allNegativationSelected) return 'mdi-checkbox-blank-outline';
      if (this.allSelected) return 'mdi-checkbox-marked';
      if (this.someSelected) return 'mdi-minus-box';
      return 'mdi-checkbox-blank-outline';
    },
    selectionLabel() {
      const itemsFromSearch = this.getFilteredValues(this.selectedValues);
      const selectedItems = itemsFromSearch.filter(item => !item.shouldIgnore).length;
      const ignoredItems = itemsFromSearch.length - selectedItems;
      const label = [];

      if (selectedItems) {
        label.push(`${selectedItems} filtrado${selectedItems > 1 ? 's' : ''}`);
      }

      if (ignoredItems) {
        label.push(`${ignoredItems} negativado${ignoredItems > 1 ? 's' : ''}`);
      }

      return label.join(', ') || 'Todos';
    },
    allNegativationSelected() {
      const itemsFromSearch = this.getFilteredValues(this.selectedValues);
      const selectedItems = itemsFromSearch.filter(item => !item.shouldIgnore).length;
      const negativationItems = itemsFromSearch.length - selectedItems;

      return negativationItems === this.items.length;
    },
    allSelected() {
      return this.items.length === this.selectedValues.length;
    },
    someSelected() {
      return this.selectedValues.length > 0 && !this.allSelected;
    },
    ignoreButtonColor() {
      if (this.someSelected) return colors.ui.steel;
      if (this.allNegativationSelected) return colors.negativeColor;
      if (this.allSelected) return colors.ui.steel;
      return colors.ui.steel;
    },
  },
  watch: {
    'value.value' (newValue) {
      if (!newValue) {
        this.getAllConsideredItems(this.items);
        this.selectedValues = [];
      }
    },
  },
  methods: {
    getItems() {
      let selectItems = [...this.selectItems];

      const valuesPreviouslyIgnored = this.getIgnoredValues();

      if (Boolean(valuesPreviouslyIgnored.length)) {
        selectItems = selectItems.map(item => this.changeItemWhenIncludesValue(item, valuesPreviouslyIgnored));
      }

      return selectItems;
    },
    getSelectedValues() {
      if (this.value && this.value.value) {
        return this.value.value.map(this.clearIgnoredItems);
      }
      return [];
    },
    getIgnoredValues() {
      if (this.value && this.value.items && Boolean(this.value.items.length)) {
        return this.value.items
          .filter(item => item.shouldIgnore)
          .map(item => item.value);
      }
      return [];
    },
    considerItem({ item }) {
      if (this.selectedValues.find(itemSelected => itemSelected === item.value)) {
        this.items = this.items.map(itemToParse => this.changeItemWithSameValue(itemToParse, item, false));
        if (!item.shouldIgnore) {
          this.selectedValues = this.selectedValues.filter(value => value !== item.value);
        }
        this.setItems(this.selectedValues);
        return;
      }
      this.toggleFilterType(item, false);
    },
    changeItemByCondition(item, condition = false, newValue) {
      if (condition) {
        item.shouldIgnore = newValue;
      }
      return item;
    },
    changeItemWithSameValue(item, itemCompare, newValue) {
      const condition = item.value === itemCompare.value;

      return this.changeItemByCondition(item, condition, newValue);
    },
    changeItemWhenIncludesValue(item, previousValue) {
      const condition = previousValue.includes(item.value);

      return this.changeItemByCondition(item, condition, true);
    },
    changeItemWhenDoNotIncludesValue(item, itemCompare) {
      const itemWithoutHyphen = item.value.startsWith('-') ? item.value.slice(1) : item.value;
      const condition = !itemCompare.includes(itemWithoutHyphen);

      return this.changeItemByCondition(item, condition, false);
    },
    ignoreItem({ item }) {
      this.toggleFilterType(item, true);
    },
    toggleFilterType(item, shouldIgnore) {
      this.items = this.items.map(itemToParse => this.changeItemWithSameValue(itemToParse, item, shouldIgnore));

      this.selectedValues.push(item.value);
      this.selectedValues = [...new Set(this.selectedValues)];
      this.setItems(this.selectedValues);
    },
    toggleItem(newValue) {
      if (this.selectAll.appearance) {
        this.selectAll.appearance = '';
      }

      this.items = this.items.map(item => this.changeItemWhenDoNotIncludesValue(item, newValue));

      this.selectedValues = newValue;
      this.setItems(newValue);
    },
    toggleAll() {
      this.selectAll.shouldIgnore = false;
      this.selectAll.appearance = '';

      return this.$nextTick(() => {
        if (this.allSelected) {
          this.items = [...this.getAllConsideredItems(this.items)];
          this.selectedValues = [];
          this.validate();
          return this.$emit('input', {
            items: [],
            value: [],
          });
        }

        const selectedItems = [...this.getAllConsideredItems(this.items)];
        this.selectedValues = [...selectedItems.map(({ value }) => value)];
        this.$emit('input', {
          items: selectedItems,
          value: this.selectedValues,
        });
        this.validate();
      });
    },
    toggleAllToNegativation() {
      this.selectAll.shouldIgnore = true;
      this.selectAll.appearance = this.$vuetify.theme.isDark ? 'to-ignore-all--dark' : 'to-ignore-all--light';

      return this.$nextTick(() => {
        this.items.forEach(item => {
          this.ignoreItem({ item });
        });
      });
    },
    getAllConsideredItems(items) {
      return items.map(item => {
        item.shouldIgnore = false;
        return item;
      });
    },
    setItems(newValue) {
      const uniqueNewValues = [...new Set(newValue)];
      const itemsFromSearch = this.getFilteredValues(uniqueNewValues);

      if (!uniqueNewValues || uniqueNewValues.length === 0) {
        this.validate();
        return this.$emit('input', undefined);
      }
      this.$emit('input', {
        items: Boolean(itemsFromSearch.length) ? itemsFromSearch : [],
        value: itemsFromSearch.map(({ value, shouldIgnore }) => shouldIgnore ? `-${value}` : this.clearIgnoredItems(value)),
      });
      this.validate();
    },
    getFilteredValues(items) {
      return this.items.filter(({ value }) => items.includes(value));
    },
    clearIgnoredItems(value) {
      if (value.indexOf('-') === 0) {
        return value.slice(1, value.length);
      }
      return value;
    },
  },
};

</script>

<style scoped lang="scss">
  .select-prepend-item {
    padding: 0 var(--ds-spacing-stack-xs) !important;
  }

  .v-select__selection.v-select__selection--comma {
    line-height: 120%;
  }

  .to-ignore-all {
    &--dark {
      background-color: rgba(95, 53, 46, 1) !important;
    }

    &--light {
      background-color: rgba(233, 176, 170, 1) !important;
    }
  }

  ::v-deep .v-list-item {
    width: 276px !important;
    max-width: 276px !important;
    padding: 0;
  }
</style>
